// src/lib/lead/extraction/runExtraction.ts
import type { ExtractedLeadV2, Lead } from "../../../types/lead";
import { applyExtractionToLead, type TextHeuristics } from "./applyExtraction";
import type { LeadDefaultFollowUpPolicy } from "@/lib/lead/extraction/leadDefaultFollowUpPolicy";
import type { MergeLeadResult } from "@/lib/lead/extraction/mergeLeadFromTranscript";

export type RunExtractionDeps = {
  extract: (text: string) => Promise<ExtractedLeadV2>;
  heuristics: TextHeuristics;
  getLeadDefaultFollowUpPolicy?: () => LeadDefaultFollowUpPolicy;
  /**
   * Phase 2 of transcript merge v2. Forwarded to `applyExtractionToLead`
   * so the shell can open the `<LeadMergeConflictModal>` when re-runs
   * collide with user-typed values.
   */
  onMergeConflicts?: (args: { leadId: string; mergeResult: MergeLeadResult; source: "extract" }) => void;
};

export type RunExtractionCtx = {
  getNextRequestId: () => number;
  isLatestRequestId: (id: number) => boolean;
  getCurrentLead: () => Lead | null;
};

export async function runExtractionOnce(
  leadId: string,
  transcript: string,
  deps: RunExtractionDeps,
  ctx: RunExtractionCtx
): Promise<Lead | null> {
  const text = (transcript || "").trim();
  if (!text) return null;

  const requestId = ctx.getNextRequestId();

  try {
    const extracted = await deps.extract(text);

    // Ignore stale results
    if (!ctx.isLatestRequestId(requestId)) return null;

    const lead = ctx.getCurrentLead();
    if (!lead || lead.id !== leadId) return null;

    return applyExtractionToLead({
      lead,
      leadId,
      transcript: text,
      extracted,
      h: deps.heuristics,
      followUpPolicy: deps.getLeadDefaultFollowUpPolicy?.(),
      onMergeConflicts: deps.onMergeConflicts,
    });
  } catch (err) {
    console.error("Extraction error (runExtractionOnce)", err);
    return null;
  }
}